/**
 * `/database/conflicts`, `/database/duplicates`, `/database/views` — RO-3-11
 * (#44304), part 4 of 4.
 *
 * Port of `excellent/app/database/conflicts/page.tsx` (55 LOC, delegating to
 * `packages/ui`'s `ConflictQueue`), `.../duplicates/page.tsx` (57 over
 * `_client.tsx`, 271) and `.../views/page.tsx` (58 over `_client.tsx`, 346).
 *
 * These three are grouped because they share one shape and one problem. Each was
 * a server component that read the vault AND passed a server ACTION down as a
 * prop — the two things a browser cannot do — and each needed a read written for
 * it (`databaseConflicts.list`, `duplicates.list`, `objectTypes.kinds`; see
 * `server/routes/database.ts`'s RO-3-11 block for all seven).
 *
 * ── Injected actions stay injected ──
 *
 * `ConflictQueue` and `DuplicateQueue` both take their write as an `actions` prop.
 * The port supplies an `apiCall`-backed function instead of a `"use server"`
 * function, and does NOT inline the call into the component. That is the design
 * those blocks were built to, stated in `conflict-queue.tsx`'s own header ("this
 * block is purely the visual + interaction layer and receives the reconcile action
 * as a prop"), and it is what lets each own its optimistic list update. The
 * signature is identical, so neither block changed at all.
 *
 * ── Workspace scoping is server-side, and that is load-bearing ──
 *
 * Both queues showed only the ACTIVE workspace's rows, with an explicit
 * `workspaceId == null → empty` guard, because the underlying `listOpenSyncConflicts`
 * / `listDuplicateCandidates` widen to the all-workspaces operator view when passed
 * `undefined`. That guard lives in the endpoint now (copied verbatim, comment and
 * all) rather than being re-expressed here — a client cannot be trusted with a
 * tenancy decision, and there is nothing for this file to get wrong.
 */

import { createRoute } from "@tanstack/react-router";
import { ConflictQueue, InfoHint, PageHeader } from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { databaseConflicts } from "@excellent/ui/api/contracts/database-conflicts";
import { duplicates } from "@excellent/ui/api/contracts/database/duplicates";
import { objectTypes } from "@excellent/ui/api/contracts/database/object-types";
import { rootRoute } from "./__root";
import { DuplicateQueue } from "./database/duplicate-queue";
import { ViewsClient } from "./database/views-client";

// ── /database/conflicts — the field-merge reconciliation queue ───────────────

function DatabaseConflicts() {
  const { data, error, isLoading } = useApi(databaseConflicts.list, {});
  const conflicts = data?.conflicts ?? [];
  const resolved = data?.resolved ?? 0;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              Sync Conflicts
              <InfoHint label="What surfaces here" side="bottom" align="start">
                When <strong>team sync</strong> is on and two machines edit the
                same high-stakes field — money, pipeline stage, lifecycle status,
                archival, or a person/organization&apos;s identity — the
                field-merge engine refuses to silently pick a winner. The
                divergence surfaces here for a human (or a second agent) to
                reconcile, the same way a different agent must verify a task
                before it is <code className="font-mono">done</code>. Everything
                else merges automatically. On a single device, or in
                last-write-wins mode, this queue stays empty.
              </InfoHint>
            </span>
          }
          description="High-stakes divergences the team-sync merge engine won't auto-resolve."
          toolbar={
            <div className="shrink-0 text-right type-subtext text-muted-foreground">
              <div className="tabular-nums text-foreground">
                {conflicts.length} open
              </div>
              <div className="tabular-nums">{resolved} resolved</div>
            </div>
          }
        />
        {error ? (
          <p className="type-body text-destructive">{error.message ?? String(error)}</p>
        ) : null}
        {isLoading && !data ? <p className="type-body text-muted-foreground">Loading…</p> : null}
        {data ? (
          <ConflictQueue
            conflicts={conflicts}
            actions={{
              resolveConflict: (args) =>
                apiCall(databaseConflicts.resolve, { body: args }),
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export const databaseConflictsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/conflicts",
  component: DatabaseConflicts,
});

// ── /database/duplicates — the duplicate merge inbox ─────────────────────────

function DatabaseDuplicates() {
  const { data, error, isLoading } = useApi(duplicates.list, {});
  const pairs = data?.pairs ?? [];
  const stubActive = data?.stubActive ?? false;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title="Duplicates"
          description="Possible duplicate records surfaced by the detector after each sync. Auto-merge never happens — review and confirm each pair."
          toolbar={
            <div className="shrink-0 text-right type-subtext text-muted-foreground">
              <div className="tabular-nums text-foreground">
                {pairs.length} {pairs.length === 1 ? "pair" : "pairs"}
              </div>
              {stubActive && (
                <div className="tabular-nums">detector: stub (#185 pending)</div>
              )}
            </div>
          }
        />
        {error ? (
          <p className="type-body text-destructive">{error.message ?? String(error)}</p>
        ) : null}
        {isLoading && !data ? <p className="type-body text-muted-foreground">Loading…</p> : null}
        {data ? (
          <DuplicateQueue
            pairs={pairs}
            stubActive={stubActive}
            actions={{
              // The origin's `mergeInto(fromUrn, toUrn)` action signature, kept
              // positional so `DuplicateQueue` itself is byte-identical.
              mergeInto: (fromUrn, toUrn) =>
                apiCall(duplicates.merge, { body: { fromUrn, toUrn } }),
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export const databaseDuplicatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/duplicates",
  component: DatabaseDuplicates,
});

// ── /database/views — saved queries over any kind ────────────────────────────

function DatabaseViews() {
  const { data, error, isLoading } = useApi(objectTypes.kinds, {});

  // `ViewsClient` renders the whole page including its `PageHeader`, so the
  // loading/error line stands alone rather than sitting inside a shell the
  // component owns. Rendering it with `kinds={[]}` would offer a kind picker with
  // nothing in it, which reads as "no kinds exist" — a claim, not a wait.
  if (error) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8">
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      </div>
    );
  }
  if ((isLoading && !data) || !data) {
    return (
      <div className="flex flex-col gap-4 px-6 py-8">
        <p className="type-body text-muted-foreground">Loading…</p>
      </div>
    );
  }
  return <ViewsClient kinds={data.kinds} />;
}

export const databaseViewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/views",
  component: DatabaseViews,
});
